import React, { useContext, useState } from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  fontWeights,
  fontSize,
} from '../styles';
import { Button, Link } from './ui';
import { ProductPrice } from './Prices';
import { StoreContext } from '../context/StoreContext';

const StyledCartCrossSellProduct = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  align-items: center;
  gap: 18px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: 110px 1fr;
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: 120px 1fr;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  max-height: 100px;
  max-width: 100px;

  ${minBreakpointQuery.smedium`
    max-height: 110px;
    max-width: 110px;
  `}

  ${minBreakpointQuery.large`
    max-height: 120px;
    max-width: 120px;
  `}
`;

const StyledDetails = styled.div`
  grid-column: 2/3;
`;

const StyledName = styled.h4`
  color: ${standardColours.black};
  font-weight: ${fontWeights.semibold};
  ${fontSize(16)};

  ${minBreakpointQuery.large`
    ${fontSize(17)};
  `}
`;

const StyledPrice = styled(ProductPrice)`
  margin-top: 6px;
`;

const StyledButton = styled(Button)`
  margin-top: 6px;
  padding: 10px 18px;
  ${fontSize(12)};
`;

const CartCrossSellProduct = ({
  title,
  handle,
  featuredImage,
  priceRangeV2,
  hasOnlyDefaultVariant,
  variants,
  isRetailProduct,
}) => {
  const { addProductsToCheckout, toggleCartOpen } = useContext(StoreContext);

  const [addingToBasket, setAddingToBasket] = useState(false);

  const handleClick = async () => {
    const lineItems = [
      {
        variantId: variants[0].storefrontId,
        quantity: 1,
      },
    ];

    setAddingToBasket(true);

    await addProductsToCheckout(lineItems);

    setAddingToBasket(false);
  };

  return (
    <StyledCartCrossSellProduct>
      <StyledImage
        image={featuredImage.gatsbyImageData}
        alt={featuredImage.altText || title}
      />
      <StyledDetails>
        <StyledName>
          <Link to={handle} onClick={() => toggleCartOpen()}>
            {title}
          </Link>
        </StyledName>
        <StyledPrice
          priceRangeV2={priceRangeV2}
          variants={variants}
          isCartCrossSellProduct={true}
          isRetailProduct={isRetailProduct && JSON.parse(isRetailProduct.value)}
        />
        <StyledButton
          {...(hasOnlyDefaultVariant
            ? {
                disabled: addingToBasket,
                onClick: () => handleClick(),
              }
            : { to: handle, onClick: () => toggleCartOpen() })}
          alt={true}
        >
          {addingToBasket
            ? 'Adding...'
            : hasOnlyDefaultVariant
            ? 'Add to my cart'
            : 'View product'}
        </StyledButton>
      </StyledDetails>
    </StyledCartCrossSellProduct>
  );
};

export default CartCrossSellProduct;

export const CartCrossSellProductdFragment = graphql`
  fragment CartCrossSellProductdFragment on ShopifyProduct {
    shopifyId
    title
    handle
    featuredImage {
      gatsbyImageData(width: 120, height: 120)
      altText
    }
    priceRangeV2 {
      minVariantPrice {
        amount
      }
      maxVariantPrice {
        amount
      }
    }
    hasOnlyDefaultVariant
    variants {
      storefrontId
      price
      compareAtPrice
    }
    isRetailProduct: metafield(key: "retail_product", namespace: "my_fields") {
      value
    }
  }
`;
